import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Eye, RotateCcw, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ROLES, INVOICE_STATUS, OBS_STATUS, DOC_STATUS, QUOTE_STATUS, PROGRESS_STATUS, CENTRAL_STATUS, fmtDate, lotIsLate, today } from "./model";
import { useEngine } from "./store";

// Barre de démonstration : voir le même projet selon le rôle (Partenaire / Entreprise / Client)
export const RoleBar = ({ current, ref = "VSL01-26-014" }) => {
  const engine = useEngine();
  if (engine.mode !== "DEMO" || process.env.NODE_ENV === "production") return null;
  const links = [
    { key: "partenaire", to: `/partenaire/projets/${ref}` },
    { key: "entreprise", to: `/entreprise` },
    { key: "client", to: `/espace-client/${ref}` },
  ];
  return (
    <div data-testid="role-bar" className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-stone-300 bg-white/70 px-3 py-2 text-[11px]">
      <span className="inline-flex items-center gap-1 font-bold uppercase tracking-[0.18em] text-stone-400"><Eye className="h-3.5 w-3.5" /> Démo · voir {ref} en tant que</span>
      {links.map((l) => (
        <Link
          key={l.key}
          to={l.to}
          data-testid={`role-bar-${l.key}`}
          className={`rounded-full px-3 py-1 font-bold transition-opacity ${current === l.key ? "text-white" : "hover:opacity-80"}`}
          style={current === l.key ? { backgroundColor: ROLES[l.key].color } : { color: ROLES[l.key].color, backgroundColor: `${ROLES[l.key].color}14` }}
        >
          {ROLES[l.key].label}
        </Link>
      ))}
      <button data-testid="role-bar-reset" onClick={engine.reset} className="ml-auto inline-flex items-center gap-1 font-bold text-stone-400 hover:text-stone-600">
        <RotateCcw className="h-3 w-3" /> Réinitialiser la démo
      </button>
    </div>
  );
};

export const RoleTag = ({ role }) => (
  <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ backgroundColor: ROLES[role]?.color || "#777" }}>{ROLES[role]?.label || role}</span>
);

const TONE = {
  a_verifier: "bg-amber-50 text-amber-700", retournee: "bg-red-50 text-red-700", a_regler: "bg-[#284E55]/10 text-[#284E55]", paiement_declare: "bg-sky-50 text-sky-700",
  paiement_confirme: "bg-sage/10 text-sage", contestee: "bg-red-50 text-red-700", a_venir: "bg-stone-100 text-stone-500",
  publiee: "bg-stone-100 text-stone-600", signale: "bg-amber-50 text-amber-700", a_traiter: "bg-amber-50 text-amber-700", en_cours: "bg-sky-50 text-sky-700", realise: "bg-sky-50 text-sky-700", cloture: "bg-sage/10 text-sage",
  a_fournir: "bg-amber-50 text-amber-700", en_verification: "bg-sky-50 text-sky-700", valide: "bg-sage/10 text-sage", refuse: "bg-red-50 text-red-700", expire: "bg-red-50 text-red-700",
  depose: "bg-stone-100 text-stone-600", a_examiner: "bg-amber-50 text-amber-700", retenu: "bg-sky-50 text-sky-700", accepte: "bg-sage/10 text-sage", preparation: "bg-sage/10 text-sage",
  validee: "bg-sage/10 text-sage", refusee: "bg-red-50 text-red-700", attente_paiement: "bg-amber-50 text-amber-700", paye: "bg-sage/10 text-sage", production: "bg-sky-50 text-sky-700", livree: "bg-sage/10 text-sage", commande: "bg-stone-100 text-stone-600", infos: "bg-stone-100 text-stone-600", a_produire: "bg-sky-50 text-sky-700",
};
const LABELS = { ...INVOICE_STATUS, ...OBS_STATUS, ...DOC_STATUS, ...QUOTE_STATUS, ...PROGRESS_STATUS, ...CENTRAL_STATUS, a_venir: "À venir", valide: "Valide" };

export const Chip = ({ status, testid, label }) => (
  <span data-testid={testid} className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold ${TONE[status] || "bg-stone-100 text-stone-600"}`}>
    {label || LABELS[status] || status}
  </span>
);

export const Bar = ({ value, color = "#284E55", ghost, testid, h = "h-2" }) => (
  <div data-testid={testid} className={`relative w-full overflow-hidden rounded-full bg-stone-100 ${h}`}>
    {ghost != null && <div className="absolute inset-y-0 left-0 rounded-full opacity-25" style={{ width: `${ghost}%`, backgroundColor: color }} />}
    <div className="absolute inset-y-0 left-0 rounded-full transition-all" style={{ width: `${value}%`, backgroundColor: color }} />
  </div>
);

// Barres d'avancement par lot — mode : partenaire (déclaré + validé), entreprise (le sien), client (validé uniquement)
export const LotProgressList = ({ project, mode, companyId, color, testid }) => {
  const lots = project.lots.filter((l) => l.amountHT > 0 && (mode !== "entreprise" || l.companyId === companyId));
  return (
    <ul data-testid={testid} className="space-y-3">
      {lots.map((l) => (
        <li key={l.id} data-testid={`${testid}-${l.id}`}>
          <div className="mb-1 flex items-center justify-between gap-3 text-sm">
            <span className="font-semibold text-navy">{l.label}{lotIsLate(l) && mode !== "client" && <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700">Retard</span>}</span>
            <span className="text-xs text-stone-500">
              {mode !== "client" && l.declaredProgress !== l.validatedProgress && <span className="mr-2 text-stone-400">déclaré {l.declaredProgress} %</span>}
              <b className="text-navy" data-testid={`${testid}-${l.id}-validated`}>{l.validatedProgress} %</b>
            </span>
          </div>
          <Bar value={l.validatedProgress} ghost={mode !== "client" ? l.declaredProgress : null} color={color} />
        </li>
      ))}
    </ul>
  );
};

export const ComplianceBadge = ({ companyId, testid }) => {
  const engine = useEngine();
  const c = engine.compliance(companyId);
  return c.ok ? (
    <span data-testid={testid} className="inline-flex items-center gap-1 rounded-full bg-sage/10 px-2.5 py-1 text-[11px] font-bold text-sage"><CheckCircle2 className="h-3 w-3" /> Documents conformes</span>
  ) : (
    <span data-testid={testid} className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-700"><AlertTriangle className="h-3 w-3" /> Intervention bloquée — documents à régulariser</span>
  );
};

// Gantt simple : une ligne par lot, échelle hebdomadaire
export const Gantt = ({ project, color = "#284E55", onSelect, selected, filterCompany, simple = false, testid = "gantt" }) => {
  const lots = project.lots.filter((l) => l.start && (!filterCompany || l.companyId === filterCompany));
  const min = lots.reduce((m, l) => (l.start < m ? l.start : m), lots[0]?.start || today());
  const max = lots.reduce((m, l) => (l.end > m ? l.end : m), lots[0]?.end || today());
  const span = Math.max(1, (new Date(max) - new Date(min)) / 86400000 + 7);
  const pct = (iso) => ((new Date(iso) - new Date(min)) / 86400000 / span) * 100;
  const weeks = Math.ceil(span / 7);
  const t = today();
  return (
    <div data-testid={testid} className="overflow-x-auto">
      <div className="min-w-[560px]">
        <div className="ml-40 flex text-[10px] text-stone-400">
          {Array.from({ length: weeks }).map((_, i) => (
            <div key={i} className="flex-1 border-l border-stone-100 pl-1">S{i + 1}</div>
          ))}
        </div>
        <ul className="space-y-1.5">
          {lots.map((l) => {
            const late = lotIsLate(l);
            return (
              <li key={l.id} className="flex items-center gap-2">
                <button
                  data-testid={`${testid}-row-${l.id}`}
                  onClick={() => onSelect?.(l)}
                  disabled={!onSelect}
                  className={`w-40 shrink-0 truncate text-left text-xs font-semibold ${selected === l.id ? "text-navy" : "text-stone-600"} ${onSelect ? "hover:text-navy" : ""}`}
                >
                  {l.label}
                </button>
                <div className="relative h-6 flex-1 rounded bg-stone-50">
                  {t >= min && t <= max && <div className="absolute inset-y-0 w-px bg-cognac" style={{ left: `${pct(t)}%` }} />}
                  <div
                    className="absolute inset-y-1 flex items-center overflow-hidden rounded-md px-1.5 text-[10px] font-bold text-white"
                    style={{ left: `${pct(l.start)}%`, width: `${Math.max(2, pct(l.end) - pct(l.start))}%`, backgroundColor: late ? "#B3413A" : color, opacity: selected && selected !== l.id ? 0.55 : 1 }}
                    title={`${fmtDate(l.start)} → ${fmtDate(l.end)}`}
                  >
                    {!simple && pct(l.end) - pct(l.start) > 22 && <span className="whitespace-nowrap">{fmtDate(l.start)} → {fmtDate(l.end)}</span>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        <p className="mt-2 text-[10px] text-stone-400">Trait cuivre : aujourd'hui · rouge : intervention en retard</p>
      </div>
    </div>
  );
};

export const AuditList = ({ ref, limit = 8, testid = "audit-list" }) => {
  const engine = useEngine();
  const events = engine.state.audit.filter((e) => e.project_id === ref).slice(0, limit);
  return (
    <ul data-testid={testid} className="divide-y divide-stone-100 text-xs">
      {events.map((e) => e.action === "project_note_added" ? (
        <li key={e.event_id} data-testid={`audit-note-${e.event_id}`} className="flex flex-wrap items-center gap-2 py-2">
          <span className="text-stone-400">{e.timestamp.replace("T", " ").slice(0, 16)}</span>
          <RoleTag role={e.role} />
          <span className="rounded-full bg-petrol-bg px-2 py-0.5 font-semibold text-petrol">Note interne</span>
          <span className="text-stone-400">{e.author_name}</span>
          {e.new_value?.title && <span className="font-semibold text-navy">{e.new_value.title}</span>}
          <span className="text-stone-600">{e.new_value?.text}</span>
        </li>
      ) : (
        <li key={e.event_id} className="flex flex-wrap items-center gap-2 py-2">
          <span className="text-stone-400">{e.timestamp.replace("T", " ").slice(0, 16)}</span>
          <RoleTag role={e.role} />
          <span className="font-semibold text-navy">{e.action}</span>
          <span className="text-stone-500">{e.entity_type} {e.entity_id}</span>
          {e.new_value != null && <span className="text-stone-500">{e.old_value != null && `${e.old_value} → `}{String(e.new_value)}</span>}
        </li>
      ))}
    </ul>
  );
};

export const History = ({ items }) => (
  <ul className="space-y-1 border-l-2 border-stone-200 pl-3 text-xs text-stone-600">
    {items.map((h, i) => (
      <li key={i} className="flex flex-wrap items-center gap-1.5"><span className="text-stone-400">{fmtDate(h.date)}</span> <RoleTag role={h.role} /> {h.text}</li>
    ))}
  </ul>
);

export const useQueryTab = (defaultTab) => {
  const { search } = useLocation();
  return new URLSearchParams(search).get("tab") || defaultTab;
};

// Champ photo : téléversement réel en mode BACKEND (stockage privé), nom de fichier simulé en mode DEMO
export const PhotoField = ({ engine, project, lotId, onChange, testid }) => {
  const backend = engine.mode === "BACKEND";
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  if (!backend) {
    return (
      <input
        data-testid={testid}
        placeholder="Nom du fichier photo (démo — aucun envoi réel)"
        className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs outline-none focus:border-petrol"
        value={label}
        onChange={(e) => { setLabel(e.target.value); onChange(e.target.value ? [e.target.value] : []); }}
      />
    );
  }
  return (
    <label data-testid={testid} className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-stone-300 px-3 py-2 text-xs font-semibold text-stone-500 hover:border-petrol hover:text-petrol">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
      {label || "Prendre / importer une photo"}
      <input
        type="file" accept="image/*" className="hidden" disabled={busy}
        onChange={async (e) => {
          const file = e.target.files[0];
          if (!file) return;
          setBusy(true);
          try {
            const doc = await engine.uploadPrivateFile(project.ref, file, "observation_photo", lotId || "pending", "PARTNER,CLIENT,COMPANY");
            setLabel(file.name);
            onChange([doc.id]);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Échec de l'envoi de la photo.");
          } finally { setBusy(false); }
        }}
      />
    </label>
  );
};

// Miniature photo : lien direct vers le fichier privé (mode BACKEND) ou pastille simulée (mode DEMO)
export const PhotoThumb = ({ id, mode, testid }) => {
  if (mode !== "BACKEND") {
    return (
      <span data-testid={testid} className="inline-flex items-center gap-1 rounded-lg bg-stone-100 px-2 py-1 text-[11px] font-semibold text-stone-500">
        <Camera className="h-3 w-3" /> {id}
      </span>
    );
  }
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/phase3/documents/${id}/download`;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testid}>
      <img src={url} alt="Photo chantier" className="h-20 w-20 rounded-lg border border-stone-200 object-cover" />
    </a>
  );
};
